import { useQuery } from '@tanstack/react-query';
import Lottie from 'react-lottie';
import { RefObject } from 'react';

import utilizationGraphLottie from '~/shared/assets/lotties/datafy-graph.json';
import { GraphResponse } from '~/shared/types/api';
import { useXFetch } from '~/shared/utils/useXFetch';
import Card from '~/shared/components/Card';
import Typography from '~/shared/components/Typography';

import LineChart, { LineChartHandle } from '../LineChart';
import RefreshFilterDropdown from '../RefreshFilterDropdown';

import { refreshStateMenuItems } from './refreshStateMenuItems';
import { CUSTOM_DATES_TITLE, dateFilterItems } from './dateFilterMenuItems';

export default function UtilizationChart({
  activeDateFilter,
  activeRefreshState,
  queryUrlString,
  onSelectRefreshState,
  // onSelectDateFilter,
  onClickRefresh,
  externalData,
  disableInternalLoader,
  chartRef,
}: {
  activeDateFilter: string;
  activeRefreshState: string;
  queryUrlString: string;
  onSelectDateFilter: (selection: (typeof dateFilterItems)[number]) => void;
  onSelectRefreshState: (selection: (typeof refreshStateMenuItems)[number]) => void;
  onClickRefresh: VoidFunction;
  externalData?: GraphResponse;
  disableInternalLoader?: boolean;
  chartRef?: RefObject<LineChartHandle>;
}) {
  const api = useXFetch();

  const queryEnabled = !externalData;
  const {
    data = {},
    isLoading,
    isRefetching,
    error,
    refetch,
  } = useQuery({
    queryKey: [queryUrlString],
    queryFn: () => api<GraphResponse>(queryUrlString),
    refetchOnWindowFocus: false,
    enabled: queryEnabled && !!queryUrlString,
  });
  const finalData = externalData || (data as GraphResponse);
  const effectiveLoading = disableInternalLoader
    ? false
    : (isLoading || isRefetching) && !externalData;

  return (
    <Card className="flex flex-col gap-[20px]">
      <div className="h-[48px] flex justify-between items-center">
        <Typography variant="H5">Utilization</Typography>
        <div className="flex gap-2">
          <RefreshFilterDropdown
            items={refreshStateMenuItems}
            selectedOptionIndex={refreshStateMenuItems.findIndex(
              (item) => activeRefreshState === item.title
            )}
            onSelect={onSelectRefreshState}
            isLoading={isLoading || isRefetching}
            onClickRefresh={() => {
              if (activeDateFilter.includes(CUSTOM_DATES_TITLE)) {
                refetch();
                return;
              }
              onClickRefresh();
            }}
          />
        </div>
      </div>
      <div className="flex-1 relative">
        <UtilizationChartImpl
          activeFilter={activeDateFilter}
          data={finalData}
          isLoading={effectiveLoading}
          error={error as Error | null}
          chartRef={chartRef}
        />
      </div>
    </Card>
  );
}

function EmptyState() {
  return (
    <Typography variant="p3" className="w-full min-h-[275.99px] flex justify-center items-center">
      No Results
    </Typography>
  );
}

function UtilizationChartImpl({
  activeFilter,
  isLoading,
  error,
  data,
  chartRef,
}: {
  activeFilter: string;
  isLoading: boolean;
  error: Error | null;
  data: GraphResponse;
  chartRef?: RefObject<LineChartHandle>;
}) {
  if (isLoading) {
    return (
      <div className="h-full min-h-[275.99px]  flex flex-col items-center justify-center">
        <Lottie
          options={{
            loop: true,
            autoplay: true,
            animationData: utilizationGraphLottie,
            rendererSettings: {
              preserveAspectRatio: 'none',
            },
          }}
          height={208.92}
          width={192}
        />
      </div>
    );
  }

  if (error || !data || !data?.utilizationGraph?.length) {
    return <EmptyState />;
  }

  return (
    <LineChart ref={chartRef} activeFilter={activeFilter} data={data.utilizationGraph} chartType="utilization" />
  );
}
